/**
 * Modern Calendar View for Installation Scheduling
 * Drag-and-drop job scheduling with staff timeline
 */

import { useState } from 'react';
import { motion } from 'framer-motion';
import {
  Calendar as CalendarIcon,
  ChevronLeft,
  ChevronRight,
  Users,
  Cloud,
  AlertTriangle,
  Plus,
  Clock
} from 'lucide-react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useSchedules, useStaff, useWeather } from '../hooks/use-scheduling';
import {
  addDays,
  startOfWeek,
  endOfWeek,
  format,
  isSameDay,
  parseISO,
  startOfMonth,
  endOfMonth,
  eachDayOfInterval
} from 'date-fns';

type ViewMode = 'week' | 'month' | 'day';

export default function SchedulingCalendar() {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [viewMode, setViewMode] = useState<ViewMode>('week');
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);

  // Fetch data
  const { data: schedules = [], isLoading: loadingSchedules } = useSchedules();
  const { data: staff = [], isLoading: loadingStaff } = useStaff();

  // Navigation
  const goToPrevious = () => {
    if (viewMode === 'week') {
      setCurrentDate(addDays(currentDate, -7));
    } else if (viewMode === 'month') {
      setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() - 1));
    } else {
      setCurrentDate(addDays(currentDate, -1));
    }
  };

  const goToNext = () => {
    if (viewMode === 'week') {
      setCurrentDate(addDays(currentDate, 7));
    } else if (viewMode === 'month') {
      setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + 1));
    } else {
      setCurrentDate(addDays(currentDate, 1));
    }
  };

  const goToToday = () => {
    setCurrentDate(new Date());
  };

  // Get date range based on view mode
  const getDateRange = () => {
    if (viewMode === 'week') {
      return {
        start: startOfWeek(currentDate, { weekStartsOn: 1 }),
        end: endOfWeek(currentDate, { weekStartsOn: 1 })
      };
    } else if (viewMode === 'month') {
      return {
        start: startOfMonth(currentDate),
        end: endOfMonth(currentDate)
      };
    } else {
      return { start: currentDate, end: currentDate };
    }
  };

  const { start: rangeStart, end: rangeEnd } = getDateRange();
  const days = eachDayOfInterval({ start: rangeStart, end: rangeEnd });

  // Get schedules for a specific date
  const getSchedulesForDate = (date: Date) => {
    return schedules.filter((schedule: any) =>
      isSameDay(parseISO(schedule.scheduledDate), date)
    );
  };

  // Header title
  const getHeaderTitle = () => {
    if (viewMode === 'month') {
      return format(currentDate, 'MMMM yyyy');
    } else if (viewMode === 'week') {
      return `${format(rangeStart, 'MMM d')} - ${format(rangeEnd, 'MMM d, yyyy')}`;
    } else {
      return format(currentDate, 'EEEE, MMMM d, yyyy');
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50 dark:from-slate-950 dark:via-slate-900 dark:to-slate-950">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="border-b border-white/20 backdrop-blur-xl bg-white/40 dark:bg-slate-900/40 sticky top-0 z-50"
      >
        <div className="container mx-auto p-6">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            {/* Title */}
            <div>
              <h1 className="text-3xl font-bold bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text text-transparent mb-2">
                Installation Calendar
              </h1>
              <p className="text-slate-600 dark:text-slate-400">
                Drag and drop jobs to reschedule
              </p>
            </div>

            {/* Actions */}
            <div className="flex items-center gap-3">
              <Button
                variant="outline"
                size="sm"
                onClick={goToToday}
                className="backdrop-blur-sm bg-white/60 dark:bg-slate-800/60"
              >
                Today
              </Button>

              <div className="flex items-center gap-1">
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={goToPrevious}
                  className="h-9 w-9"
                >
                  <ChevronLeft className="h-5 w-5" />
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={goToNext}
                  className="h-9 w-9"
                >
                  <ChevronRight className="h-5 w-5" />
                </Button>
              </div>

              <Button
                className="bg-gradient-to-r from-blue-600 to-indigo-600 text-white hover:from-blue-700 hover:to-indigo-700"
              >
                <Plus className="w-4 h-4 mr-2" />
                New Job
              </Button>
            </div>
          </div>

          {/* View Controls */}
          <div className="flex items-center justify-between mt-4">
            <h2 className="text-xl font-semibold text-slate-700 dark:text-slate-300">
              {getHeaderTitle()}
            </h2>

            <Tabs value={viewMode} onValueChange={(v) => setViewMode(v as ViewMode)}>
              <TabsList className="backdrop-blur-sm bg-white/60 dark:bg-slate-800/60">
                <TabsTrigger value="day">Day</TabsTrigger>
                <TabsTrigger value="week">Week</TabsTrigger>
                <TabsTrigger value="month">Month</TabsTrigger>
              </TabsList>
            </Tabs>
          </div>
        </div>
      </motion.div>

      {/* Calendar Content */}
      <div className="container mx-auto p-6">
        {viewMode === 'week' && (
          <WeekView
            days={days}
            schedules={schedules}
            staff={staff}
            getSchedulesForDate={getSchedulesForDate}
          />
        )}

        {viewMode === 'month' && (
          <MonthView
            days={days}
            currentMonth={currentDate}
            schedules={schedules}
            getSchedulesForDate={getSchedulesForDate}
          />
        )}

        {viewMode === 'day' && (
          <DayView
            date={currentDate}
            schedules={getSchedulesForDate(currentDate)}
            staff={staff}
          />
        )}
      </div>
    </div>
  );
}

// Week View Component
function WeekView({
  days,
  schedules,
  staff,
  getSchedulesForDate
}: {
  days: Date[];
  schedules: any[];
  staff: any[];
  getSchedulesForDate: (date: Date) => any[];
}) {
  return (
    <Card className="border-0 shadow-xl backdrop-blur-xl bg-white/60 dark:bg-slate-900/60 overflow-hidden">
      {/* Day Headers */}
      <div className="grid grid-cols-7 border-b border-slate-200 dark:border-slate-700">
        {days.slice(0, 7).map((day) => (
          <div
            key={day.toISOString()}
            className="p-4 text-center border-r border-slate-200 dark:border-slate-700 last:border-r-0"
          >
            <div className="text-sm font-medium text-slate-600 dark:text-slate-400">
              {format(day, 'EEE')}
            </div>
            <div className={`text-2xl font-bold mt-1 ${
              isSameDay(day, new Date())
                ? 'text-blue-600 dark:text-blue-400'
                : 'text-slate-900 dark:text-slate-100'
            }`}>
              {format(day, 'd')}
            </div>
          </div>
        ))}
      </div>

      {/* Staff Rows */}
      <div className="divide-y divide-slate-200 dark:divide-slate-700">
        {staff.filter((s: any) => s.isActive).map((staffMember: any) => (
          <div key={staffMember.id} className="grid grid-cols-7">
            {/* Staff Info */}
            <div className="col-span-7 p-3 bg-slate-50 dark:bg-slate-800/50 flex items-center gap-3">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-full bg-gradient-to-r from-blue-500 to-indigo-500 flex items-center justify-center text-white text-sm font-bold">
                  {staffMember.user?.fullName?.[0] || 'S'}
                </div>
                <div>
                  <div className="font-medium text-sm">
                    {staffMember.user?.fullName || staffMember.user?.username}
                  </div>
                  <div className="text-xs text-slate-600 dark:text-slate-400">
                    {staffMember.jobRole.replace(/_/g, ' ')}
                  </div>
                </div>
              </div>
            </div>

            {/* Day Cells */}
            {days.slice(0, 7).map((day) => {
              const daySchedules = getSchedulesForDate(day).filter((s: any) =>
                s.assignments?.some((a: any) => a.staffId === staffMember.id)
              );

              return (
                <div
                  key={`${staffMember.id}-${day.toISOString()}`}
                  className="min-h-[120px] p-2 border-r border-slate-200 dark:border-slate-700 last:border-r-0"
                >
                  {daySchedules.map((schedule: any) => (
                    <JobCard key={schedule.id} schedule={schedule} />
                  ))}
                </div>
              );
            })}
          </div>
        ))}
      </div>
    </Card>
  );
}

// Month View Component
function MonthView({
  days,
  currentMonth,
  schedules,
  getSchedulesForDate
}: {
  days: Date[];
  currentMonth: Date;
  schedules: any[];
  getSchedulesForDate: (date: Date) => any[];
}) {
  // Group days into weeks
  const weeks: Date[][] = [];
  for (let i = 0; i < days.length; i += 7) {
    weeks.push(days.slice(i, i + 7));
  }

  return (
    <Card className="border-0 shadow-xl backdrop-blur-xl bg-white/60 dark:bg-slate-900/60 overflow-hidden">
      {/* Day Headers */}
      <div className="grid grid-cols-7 border-b border-slate-200 dark:border-slate-700">
        {['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].map((day) => (
          <div
            key={day}
            className="p-3 text-center text-sm font-medium text-slate-600 dark:text-slate-400 border-r border-slate-200 dark:border-slate-700 last:border-r-0"
          >
            {day}
          </div>
        ))}
      </div>

      {/* Week Rows */}
      {weeks.map((week, weekIndex) => (
        <div key={weekIndex} className="grid grid-cols-7 border-b border-slate-200 dark:border-slate-700 last:border-b-0">
          {week.map((day) => {
            const daySchedules = getSchedulesForDate(day);
            const isCurrentMonth = day.getMonth() === currentMonth.getMonth();
            const isToday = isSameDay(day, new Date());

            return (
              <div
                key={day.toISOString()}
                className={`min-h-[120px] p-2 border-r border-slate-200 dark:border-slate-700 last:border-r-0 ${
                  !isCurrentMonth ? 'bg-slate-50/50 dark:bg-slate-800/20' : ''
                }`}
              >
                <div className={`text-sm font-medium mb-1 ${
                  isToday
                    ? 'text-blue-600 dark:text-blue-400 font-bold'
                    : isCurrentMonth
                    ? 'text-slate-900 dark:text-slate-100'
                    : 'text-slate-400 dark:text-slate-600'
                }`}>
                  {format(day, 'd')}
                </div>

                {daySchedules.length > 0 && (
                  <div className="space-y-1">
                    {daySchedules.slice(0, 2).map((schedule: any) => (
                      <div
                        key={schedule.id}
                        className="text-xs p-1 rounded bg-blue-500/10 text-blue-700 dark:text-blue-300 truncate cursor-pointer hover:bg-blue-500/20"
                      >
                        {schedule.scheduledStartTime}
                      </div>
                    ))}
                    {daySchedules.length > 2 && (
                      <div className="text-xs text-slate-600 dark:text-slate-400">
                        +{daySchedules.length - 2} more
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      ))}
    </Card>
  );
}

// Day View Component
function DayView({
  date,
  schedules,
  staff
}: {
  date: Date;
  schedules: any[];
  staff: any[];
}) {
  const hours = Array.from({ length: 24 }, (_, i) => i);

  return (
    <Card className="border-0 shadow-xl backdrop-blur-xl bg-white/60 dark:bg-slate-900/60 overflow-hidden">
      <div className="p-6">
        <div className="grid grid-cols-12 gap-4">
          {/* Time Column */}
          <div className="col-span-1">
            {hours.map((hour) => (
              <div key={hour} className="h-20 text-sm text-slate-600 dark:text-slate-400">
                {format(new Date().setHours(hour, 0), 'HH:mm')}
              </div>
            ))}
          </div>

          {/* Schedule Grid */}
          <div className="col-span-11 relative">
            {hours.map((hour) => (
              <div
                key={hour}
                className="h-20 border-b border-slate-200 dark:border-slate-700"
              />
            ))}

            {/* Job Cards */}
            {schedules.map((schedule: any) => (
              <JobCard key={schedule.id} schedule={schedule} detailed />
            ))}
          </div>
        </div>
      </div>
    </Card>
  );
}

// Job Card Component
function JobCard({ schedule, detailed = false }: { schedule: any; detailed?: boolean }) {
  const hasConflicts = schedule.conflicts && schedule.conflicts.length > 0;

  return (
    <motion.div
      whileHover={{ scale: 1.02 }}
      className={`p-2 rounded-lg cursor-move shadow-sm ${
        hasConflicts
          ? 'bg-red-500/10 border border-red-500/20'
          : 'bg-blue-500/10 border border-blue-500/20'
      }`}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="flex-1 min-w-0">
          <div className="text-xs font-medium truncate">
            {schedule.installation?.installationAddress || 'No address'}
          </div>
          <div className="text-xs text-slate-600 dark:text-slate-400 flex items-center gap-1 mt-1">
            <Clock className="w-3 h-3" />
            {schedule.scheduledStartTime} - {schedule.scheduledEndTime}
          </div>
        </div>

        {hasConflicts && (
          <AlertTriangle className="w-4 h-4 text-red-600 flex-shrink-0" />
        )}
      </div>

      {detailed && schedule.assignments && schedule.assignments.length > 0 && (
        <div className="mt-2 flex items-center gap-1 text-xs">
          <Users className="w-3 h-3" />
          <span>{schedule.assignments.length} crew</span>
        </div>
      )}

      <Badge
        variant="secondary"
        className="mt-2 text-xs"
      >
        {schedule.status}
      </Badge>
    </motion.div>
  );
}
